import { Car, Check, Zap, CalendarCheck } from "lucide-react";

/**
 * Automotive hero visual: a dealer lead handled end-to-end, on loop.
 * A VDP lead lands, the AI answers in seconds, the test drive gets booked.
 * Pure CSS loop (~11s), Demo-labeled, prerender + reduced-motion safe
 * (static fully-assembled state).
 */
export function AutomotiveCommandVisual() {
  return (
    <div className="relative mx-auto w-full max-w-[500px] select-none">
      <style>{`
        @keyframes acv-in {
          0%, 100% { opacity: 0; transform: translateY(10px); }
          6%, 88% { opacity: 1; transform: translateY(0); }
          94% { opacity: 0; transform: translateY(-6px); }
        }
        .acv-s1 { animation: acv-in 11s cubic-bezier(0.16,1,0.3,1) infinite; }
        .acv-s2 { animation: acv-in 11s cubic-bezier(0.16,1,0.3,1) 1.6s infinite; opacity: 0; }
        .acv-s3 { animation: acv-in 11s cubic-bezier(0.16,1,0.3,1) 3.4s infinite; opacity: 0; }
        .acv-s4 { animation: acv-in 11s cubic-bezier(0.16,1,0.3,1) 5s infinite; opacity: 0; }
        @keyframes acv-dot {
          0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.5); }
          70% { box-shadow: 0 0 0 8px rgba(16,185,129,0); }
        }
        .acv-live { animation: acv-dot 2.4s infinite; }
        @media (prefers-reduced-motion: reduce) {
          .acv-s1, .acv-s2, .acv-s3, .acv-s4 { animation: none; opacity: 1; transform: none; }
          .acv-live { animation: none; }
        }
      `}</style>

      <div className="absolute -inset-8 -z-10 bg-[radial-gradient(circle_at_50%_35%,rgba(47,95,232,0.30),transparent_65%)] blur-2xl" />

      <div className="relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-white shadow-[0_24px_60px_-20px_rgba(0,0,0,0.5)]">
        {/* header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#101319] text-white">
              <Zap className="h-4 w-4" fill="currentColor" />
            </div>
            <div>
              <div className="text-sm font-bold tracking-tight text-slate-900">Dealer lead desk</div>
              <div className="text-[11px] font-medium text-slate-500">Every inquiry answered. Sample view.</div>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 font-mono-ibm rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-600">
            <span className="acv-live h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Demo
          </span>
        </div>

        {/* the lead */}
        <div className="acv-s1 border-b border-slate-100 px-5 py-3.5">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-14 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-400">
              <Car className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-[13px] font-bold text-slate-900">2021 F-150 XLT · $38,995</span>
                <span className="font-mono-ibm shrink-0 text-[9px] font-bold uppercase tracking-[0.1em] text-slate-500">New lead · 9:42 PM</span>
              </div>
              <div className="text-[11.5px] text-slate-500">Marketplace inquiry · after hours</div>
            </div>
          </div>
        </div>

        {/* conversation */}
        <div className="space-y-2.5 px-5 py-4">
          <div className="acv-s2 max-w-[80%] rounded-2xl rounded-bl-md bg-slate-100 px-3.5 py-2.5 text-[13px] font-medium text-slate-800">
            Is the F-150 still available? What's the best price?
          </div>
          <div className="acv-s3 ml-auto max-w-[85%] rounded-2xl rounded-br-md bg-[#2F5FE8] px-3.5 py-2.5 text-[13px] font-medium text-white shadow-md shadow-[#2F5FE8]/25">
            It is, and it just had new brakes. Want to take it out tomorrow? I have 10:30 or 4:15 open.
          </div>
          <div className="acv-s4 flex justify-center pt-1">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-[11px] font-bold text-emerald-700">
              <CalendarCheck className="h-3.5 w-3.5" />
              Test drive booked: tomorrow 4:15 PM
            </span>
          </div>
        </div>

        {/* footer */}
        <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50/70 px-5 py-3">
          <Check className="h-3.5 w-3.5 text-emerald-500" />
          <span className="text-[11px] font-medium text-slate-500">Replied in 8s · logged to CRM · salesperson briefed for the morning</span>
        </div>
      </div>

      {/* floating accents */}
      <div className="absolute -top-4 -right-3 z-10 rounded-full bg-[#2F5FE8] px-3.5 py-1.5 text-xs font-bold text-white shadow-lg shadow-[#2F5FE8]/30">
        Answered while you slept
      </div>
    </div>
  );
}

export default AutomotiveCommandVisual;
